import Config from "vamos/cockpit/config/Config";
import BaseController from "vamos/cockpit/base/controller/type/BaseController";
import ApiService from "vamos/cockpit/base/services/type/ApiService";
import MaintenanceService from "vamos/cockpit/main/app/service/MaintenanceService";
import LoginService from "vamos/cockpit/login/LoginService";
import Storage from "sap/ui/util/Storage";
import JSONModel from "sap/ui/model/json/JSONModel";
import UserHandler from "vamos/cockpit/data/user/handler/UserHandler";
import ViewHandler from "vamos/cockpit/base/handler/ViewHandler";
import { URLHelper } from "sap/m/library";
import TabContainer, { TabContainer$ItemCloseEvent, TabContainer$ItemSelectEvent } from "sap/m/TabContainer";
import TabContainerItem from "sap/m/TabContainerItem";
import CustomData from "sap/ui/core/CustomData";
import FlexibleColumnLayout from "sap/f/FlexibleColumnLayout";
import { Router$BeforeRouteMatchedEvent } from "sap/ui/core/routing/Router";
import Target from "sap/ui/core/routing/Target";
import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import { Menu$ItemSelectedEvent } from "sap/m/Menu";
import MenuItem, { MenuItem$PressEvent } from "sap/m/MenuItem";
import { Button$PressEvent } from "sap/m/Button";
import BusyIndicator from "sap/ui/core/BusyIndicator";
import { ResponseLong } from "vamos/cockpit/model/api/vamos.private.api.types";
import MessagePopoverController from "vamos/cockpit/main/app/controller/MessagePopover.controller";
import AuthService from "vamos/cockpit/base/services/type/AuthService";

interface StoredTab {
    name: string;
    actualRoute: string;
    history: string[];
}

type TargetWithOptions = Target & { _oOptions: { controlId?: string } };

/**
 * @namespace vamos.cockpit.main.app.controller
 */
export default class App extends BaseController {
    private readonly STORED_TABS_NAME = "storedTabs";

    private maintenanceService: MaintenanceService = null;

    private sessionTabStorage: Storage;

    private unloadEventName: string;

    private readonly unloadHandler = () => this.beforeWindowUnload();

    public onInit(): void {
        super.onInit();
        this.maintenanceService = new MaintenanceService(this);
        const tabContainer = this.getView().byId("TabContainer") as TabContainer;
        // tabContainer is for ts
        this.getComponent().setTabContainer(tabContainer);
        const router = this.getRouter();
        this.handleTabsFromStorage();
        this.getView().setModel(new JSONModel({ count: 0 }), "messageBadge");
        router.attachBeforeRouteMatched(this.onBeforeRouteMatched, this);
        router.initialize();
        const isOnIOS = navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPhone/i);
        this.unloadEventName = isOnIOS ? "pagehide" : "beforeunload";
        window.addEventListener(this.unloadEventName, this.unloadHandler);
        this.updateNewMessageBadge();
        this.getComponent()
            .getModel("app")
            .bindProperty("/Clients/CurrentClientName")
            .attachChange(() => {
                void this.maintenanceService.checkMaintenanceStatus();
            });
        void this.maintenanceService.checkMaintenanceStatus();
    }

    public onBeforeRouteMatched(oEvent: Router$BeforeRouteMatchedEvent): void {
        if (oEvent.getParameter("name") === "mainMenu") {
            this.getTabContainer().setSelectedItem(this.getTabContainerItemByKey("mainMenu"));
            return;
        }

        //TODO: Refactor routing
        const oConfig = oEvent.getParameter("config") as { name: string };
        const sRoutePath = oConfig["name"];
        const oArguments = oEvent.getParameter("arguments") as { tab?: string };
        const sTabKey = oArguments?.tab ? oArguments.tab : sRoutePath;
        let oTabItem = this.getTabContainerItemByKey(sTabKey);
        oTabItem = oTabItem ? oTabItem : this.addNewTabContainerItem(sTabKey);
        this.showTabContent(oTabItem, sTabKey, sRoutePath);
    }

    public showTabContent(oTabItem: TabContainerItem, sTabKey: string, sRoutePath: string): void {
        const oTargetConfig = (this.getRouter().getTarget(sRoutePath) as TargetWithOptions)._oOptions;
        const oErrorConfig = (this.getRouter().getTarget("error") as TargetWithOptions)._oOptions;
        if (!oTabItem) {
            console.error("no route for key" + sTabKey);
            return;
        }
        oTabItem.data("routeHash", this.getHash());
        this.getTabContainer().setSelectedItem(oTabItem);
        const oLayout = oTabItem.getContent()[0];
        if (oLayout.getId() === "idAppControl") {
            return;
        }
        oTargetConfig.controlId = oLayout.getId();
        oErrorConfig.controlId = oLayout.getId();
    }

    public addNewTabContainerItem(sKey: string, sNoCloseButton = "false", aHistory: string[] = [], sRouteHash?: string): TabContainerItem {
        const oItem = new TabContainerItem({
            key: sKey,
            name: this.getText(sKey),
            customData: new CustomData({
                key: "noCloseButton",
                writeToDom: true,
                value: sNoCloseButton,
            }),
            content: [new FlexibleColumnLayout({ autoFocus: false })],
        });
        if (aHistory.length === 0) {
            aHistory.push(sRouteHash || sKey);
        }
        oItem.data("history", aHistory);
        oItem.data("routeHash", sRouteHash || sKey);
        this.getTabContainer().addItem(oItem);
        return oItem;
    }

    public beforeWindowUnload(): void {
        const oTabContainer = this.getTabContainer();
        const mTabMap: Record<string, StoredTab> = {};
        oTabContainer.getItems().forEach((oItem) => {
            const sKey = oItem.getKey();
            if (sKey === "mainMenu") {
                return;
            }
            mTabMap[sKey] = {
                name: oItem.getName(),
                actualRoute: oItem.data("routeHash") as string,
                history: oItem.data("history") as string[],
            };
        });
        this.getSessionStorage().put(this.STORED_TABS_NAME, mTabMap);
    }

    public async onPressMyProfile(): Promise<void> {
        const user = await UserHandler.getInstance().getUser();
        if (user.IsCiamUser) {
            URLHelper.redirect(Config.getConfigValue("CiamProfilePage") as string, true);
        } else {
            this.getRouter().navTo("userSettings", {}, true);
        }
    }

    public onLogout(): void {
        this.getSessionStorage().clear();
        window.removeEventListener(this.unloadEventName, this.unloadHandler);
        // redirect to logout page
        const loginService = new LoginService();
        loginService.logoutUser();
    }

    public async onClientSelect(event: Menu$ItemSelectedEvent): Promise<void> {
        const item = event.getParameter("item") as MenuItem;
        const clientGuid = item.getKey();
        BusyIndicator.show();
        try {
            await ApiService.getInstance().postRestJsonData("user/setClient", { clientGuid: clientGuid });
            AuthService.setClientGuid(clientGuid);
        } catch (e) {
            console.error(e);
            return;
        } finally {
            BusyIndicator.hide();
        }
        const oComponent = this.getComponent();
        this.refreshWholeModel();
        await Promise.all([
            UserHandler.getInstance().loadUser().then(() => oComponent.refreshUser()),
            oComponent.refreshClientList(),
        ]);
        // setSelected is not part of the public MenuItem typings
        item.setProperty("selected", true);
    }

    public onItemSelect(oEvent: TabContainer$ItemSelectEvent): void {
        const oItem = oEvent.getParameter("item");

        (this.getTabContainer() as TabContainer & { _oItem?: TabContainerItem })._oItem = oItem;
        this.getRouter()
            .getHashChanger()
            .setHash((oItem.data("routeHash") as string) || "");
        (this.getModel() as ODataModel).resetChanges();

    }

    public onItemClose(oEvent: TabContainer$ItemCloseEvent): void {
        const oTabContainer = this.getTabContainer() as TabContainer & { _oItem?: TabContainerItem };
        if (oEvent.getParameter("item").getId() !== oTabContainer.getSelectedItem()) {
            return;
        }
        // select main menu before the item gets removed, so the TabContainer does not select the neighbour tab
        const oMainMenuItem = this.getTabContainerItemByKey("mainMenu");
        oTabContainer.setSelectedItem(oMainMenuItem);
        oTabContainer._oItem = oMainMenuItem;
        this.getRouter()
            .getHashChanger()
            .setHash((oMainMenuItem.data("routeHash") as string) || "");
        (this.getModel() as ODataModel).resetChanges();
    }

    public handleTabsFromStorage(): void {
        const sRoute = this.getRouter().getHashChanger().getHash();
        const oStorage = this.getSessionStorage().get(this.STORED_TABS_NAME) as Record<string, StoredTab>;
        if (!oStorage) {
            return;
        }
        for (const sKey in oStorage) {
            const oKeyData = oStorage[sKey];
            const sItemHash = oKeyData.actualRoute;
            const oItem = this.addNewTabContainerItem(sKey, "false", oKeyData.history, sItemHash);
            if (sRoute === sKey) {
                this.getTabContainer().setSelectedItem(oItem);
                this.showTabContent(oItem, sKey, sKey);
            }
        }

    }

    public updateNewMessageBadge(): void {
        ApiService.getInstance()
            .getRestJsonData<ResponseLong>("/message/new-count")
            .then((response) => {
                (this.getView().getModel("messageBadge") as JSONModel).setProperty("/count", response.content);
            })
            .catch((e) => console.error(e));

    }

    public getSessionStorage(): Storage {
        if (!this.sessionTabStorage) {
            this.sessionTabStorage = new Storage(Storage.Type.session, "tabStorage");
        }
        return this.sessionTabStorage;
    }

    public onOpenLink(oEvent: MenuItem$PressEvent): void {
        const sLink = oEvent.getSource().data("link") as string;
        window.open(sLink, "_blank");
    }

    public async onOpenNotifications(oEvent: Button$PressEvent): Promise<void> {
        const button = oEvent.getSource();
        const view = await ViewHandler.getView("vamos.cockpit.main.app.view.MessagePopover", this.getComponent());
        const controller = view.getController() as MessagePopoverController;
        await controller.openDialog(button, () => {
            this.updateNewMessageBadge();
        });
    }
}
